import axios from 'axios'
import { getTprmApiBaseUrl, getApiOrigin } from '../utils/backendEnv'

const strip = (value = '') => value.replace(/\/+$/, '')

const tprmApiRoot = () => strip(getTprmApiBaseUrl())

/**
 * Optional GRC origin when TPRM and GRC share auth (hybrid). Ignored if unset/invalid.
 */
const grcOriginForRoleFallback = () => {
  const raw = (import.meta.env.VITE_GRC_API_ORIGIN || '').trim()
  if (!raw) return ''
  try {
    return strip(new URL(raw).origin)
  } catch {
    return strip(raw)
  }
}

/**
 * Get user role from RBAC system
 * @param {string} token - JWT session token
 * @returns {Promise<{success: boolean, role?: string, error?: string}>}
 */
const CACHED_ROLE_KEY = 'tprm_user_role'

export function getCachedUserRole() {
  try {
    return localStorage.getItem(CACHED_ROLE_KEY) || ''
  } catch (e) {
    return ''
  }
}

export function setCachedUserRole(role) {
  try {
    if (role) {
      localStorage.setItem(CACHED_ROLE_KEY, String(role).toLowerCase())
    } else {
      localStorage.removeItem(CACHED_ROLE_KEY)
    }
  } catch (e) {
    /* ignore */
  }
}

export function clearCachedUserRole() {
  try {
    localStorage.removeItem(CACHED_ROLE_KEY)
  } catch (e) {
    /* ignore */
  }
}

export function isVendorUser() {
  const cached = getCachedUserRole()
  if (cached) return cached === 'vendor'
  // Fallback: check local user objects
  try {
    let userStr = localStorage.getItem('current_user')
    if (!userStr) userStr = localStorage.getItem('user')
    if (userStr) {
      const user = JSON.parse(userStr)
      const role = String(user?.role || '').toLowerCase()
      if (role === 'vendor') return true
    }
  } catch (e) {
    /* ignore */
  }
  return false
}

export async function getUserRole(token) {
  const headers = {
    'Authorization': `Bearer ${token}`,
    'Content-Type': 'application/json'
  }

  const base = tprmApiRoot()
  const roleEndpoints = [`${base}/rbac/role/`]

  const grcOrigin = grcOriginForRoleFallback()
  if (grcOrigin) {
    roleEndpoints.push(
      `${grcOrigin}/api/grc/rbac/user-role/`,
      `${grcOrigin}/api/grc/user-role/`,
      `${grcOrigin}/api/user-role/`,
      `${grcOrigin}/api/rbac/role/`
    )
  } else {
    const origin = strip(getApiOrigin())
    if (origin) {
      roleEndpoints.push(
        `${origin}/api/grc/rbac/user-role/`,
        `${origin}/api/grc/user-role/`,
        `${origin}/api/user-role/`,
        `${origin}/api/rbac/role/`
      )
    }
  }

  let lastError = null
  for (const endpoint of roleEndpoints) {
    try {
      const response = await axios.get(endpoint, { headers })
      const data = response?.data || {}
      const role =
        data.role ||
        data.user_role ||
        data.userRole ||
        data.data?.role ||
        data.data?.user_role ||
        data.data?.userRole

      if (role) {
        setCachedUserRole(role)
        return { success: true, role }
      }

      if (data.success && data.role) {
        setCachedUserRole(data.role)
        return { success: true, role: data.role }
      }
    } catch (error) {
      lastError = error
      // Try next endpoint on 404/405; stop early on auth issues.
      const status = error?.response?.status
      if (status === 401 || status === 403) {
        break
      }
    }
  }

  return {
    success: false,
    error: lastError?.response?.data?.message || 'Failed to get user role'
  }
}

/**
 * Get vendor registration status (lifecycle stage)
 * @param {number} userId - User ID
 * @param {string} token - JWT session token
 * @returns {Promise<{success: boolean, stageId?: number, error?: string}>}
 */
export async function getVendorRegistrationStatus(userId, token) {
  try {
    const base = tprmApiRoot()
    const response = await axios.get(
      `${base}/v1/vendor-core/temp-vendors/get_user_data/`,
      {
        params: { user_id: userId },
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      }
    )
    
    if (response.data.status === 'success' || response.data.status === 'partial_success') {
      const lifecycleData = response.data.data?.lifecycle
      if (lifecycleData && lifecycleData.current_stage) {
        return {
          success: true,
          stageId: lifecycleData.current_stage.stage_id
        }
      } else {
        // No lifecycle data means registration not started (stage 1)
        return {
          success: true,
          stageId: 1
        }
      }
    } else if (response.status === 404) {
      // No vendor data found means registration not started (stage 1)
      return {
        success: true,
        stageId: 1
      }
    } else {
      return {
        success: false,
        error: response.data.message || 'Failed to get registration status'
      }
    }
  } catch (error) {
    if (error.response?.status === 404) {
      // No vendor data found means registration not started (stage 1)
      return {
        success: true,
        stageId: 1
      }
    }

    return {
      success: false,
      error: error.response?.data?.message || 'Failed to get registration status'
    }
  }
}

/**
 * Determine the post-login route based on user role and registration status
 * @param {string} token - JWT session token
 * @param {number} userId - User ID (optional, will be extracted from token if not provided)
 * @returns {Promise<string>} Route path
 */
export async function getPostLoginRoute(token, userId = null) {
  try {
    // Get user role from RBAC
    const roleResult = await getUserRole(token)
    
    if (!roleResult.success) {

      return '/'
    }

    const userRole = roleResult.role?.toLowerCase()

    // If role is "Vendor", check registration status
    if (userRole === 'vendor') {
      // Get user ID from token or use provided userId
      if (!userId) {
        try {
          // Try to get user ID from localStorage (user object has 'userid' field)
          const currentUser = JSON.parse(localStorage.getItem('current_user') || '{}')
          userId = currentUser.userid || currentUser.id || currentUser.user_id
          
          // Also try to extract from token payload if available
          if (!userId && token) {
            try {
              const tokenParts = token.split('.')
              if (tokenParts.length === 3) {
                const payload = JSON.parse(atob(tokenParts[1]))
                userId = payload.user_id || payload.userid || payload.id
              }
            } catch (e) {

            }
          }
        } catch (e) {

        }
      }
      
      if (!userId) {

        return '/vendor-registration'
      }

      // Get registration status
      const statusResult = await getVendorRegistrationStatus(userId, token)

      if (!statusResult.success) {

        return '/vendor-registration'
      }

      const stageId = statusResult.stageId

      // If registration completed (stage !== 1), redirect to questionnaire response
      // If registration not completed (stage === 1), redirect to registration
      if (stageId !== 1) {

        return '/vendor-questionnaire-response'
      } else {

        return '/vendor-registration'
      }
    } else {
      // TPRM home is route "/" (HomePage); /home is not registered unless aliased

      return '/'
    }
  } catch (error) {

    return '/'
  }
}

export default {
  getUserRole,
  getVendorRegistrationStatus,
  getPostLoginRoute,
  getCachedUserRole,
  setCachedUserRole,
  clearCachedUserRole,
  isVendorUser
}

